import { Archive, Ban, CircleCheckBig, Eraser, Eye, Mail } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AGENT_MARKS } from '@/lib/types';
import type { AgentMark } from '@/lib/types';

/**
 * How each triage mark presents. The single source of truth for the marks'
 * icons, labels, and colors, so the row icon, the quick-action menu, and the
 * filter can never disagree about what `in_review` is called or looks like.
 *
 * Icons are deliberately distinct in silhouette from the activity icons they
 * replace ({@link AgentActivityIcon}): a marked row must not read as a live
 * state at a glance. Colors reuse the semantic status tokens so marks sit in
 * the same palette as everything else in the list.
 */
export const AGENT_MARK_CONFIG: Record<
  AgentMark,
  { label: string; icon: LucideIcon; className: string }
> = {
  blocked: {
    label: 'Blocked',
    icon: Ban,
    className: 'text-status-error',
  },
  in_review: {
    label: 'In review',
    icon: Eye,
    className: 'text-status-warning',
  },
  completed: {
    label: 'Completed',
    icon: CircleCheckBig,
    className: 'text-status-info',
  },
};

/**
 * One entry in the run's state picker. `mark` sets a mark, `archive` retires the
 * run, `clear` removes the applied mark, `unread` puts the run back in the
 * viewer's unread state — they all answer the same question ("what is this run
 * to me right now?"), which is why they share one flat menu.
 */
export type AgentStateChoice = {
  kind: 'mark' | 'clear' | 'archive' | 'unread';
  /** Stable key for React and test ids. */
  key: string;
  label: string;
  icon: LucideIcon;
  iconClassName: string;
  /** The mark this entry applies; null for `clear` and `archive`. */
  mark: AgentMark | null;
  /** This entry is the run's current state. */
  checked: boolean;
};

/**
 * Every choice the state picker offers, in order, for a run carrying `current`.
 *
 * Shared by the sidebar row's menu and the agent action list so the two can
 * never disagree about ordering or which entry is checked.
 *
 * The states read as one flat list — `Archived` is a peer of the marks, not a
 * separate kind of thing, even though it is persisted as its own column rather
 * than as a mark value. It is phrased as a state (`Archived`) to match its
 * neighbours; the row's standalone quick-action button still says `Archive`,
 * because that is a button that performs an action rather than a state you pick.
 * Its icon stays neutral for the same reason `stopped` is neutral in the
 * activity palette: filing a run away is not an alarm.
 *
 * `clear` sits last and appears only when there is something to clear. It uses
 * an eraser rather than a slashed circle, which would be nearly
 * indistinguishable from `blocked`'s icon in the same menu. Re-picking the
 * applied mark clears it too, so this is the discoverable spelling of a gesture
 * that already works, not the only way to do it.
 */
export function agentStateChoices(current: AgentMark | null | undefined): AgentStateChoice[] {
  const choices: AgentStateChoice[] = AGENT_MARKS.map((mark) => ({
    kind: 'mark' as const,
    key: `mark-${mark}`,
    label: AGENT_MARK_CONFIG[mark].label,
    icon: AGENT_MARK_CONFIG[mark].icon,
    // All mark glyphs are Lucide full-bleed; trim them in shared menu columns
    // so the icon→label gap matches Pin / Folder / Trash neighbours. Keep this
    // off AGENT_MARK_CONFIG.className — AgentActivityIcon already applies it.
    iconClassName: `${AGENT_MARK_CONFIG[mark].className} icon-full-bleed`,
    mark,
    checked: current === mark,
  }));

  choices.push({
    kind: 'archive',
    key: 'archive',
    label: 'Archived',
    icon: Archive,
    iconClassName: 'text-muted-foreground',
    mark: null,
    checked: false,
  });

  // Unread is the odd one out: it is per-viewer read state rather than a
  // persisted, mutually-exclusive run state, so it never carries a check and
  // picking it does not displace whatever mark the run has. It lives here
  // anyway because "mark this as unread" is the same gesture users already
  // reach for in this menu, and a sibling top-level entry read as a second,
  // competing way to mark a run.
  choices.push({
    kind: 'unread',
    key: 'mark-unread',
    label: 'Unread',
    icon: Mail,
    iconClassName: 'text-muted-foreground',
    mark: null,
    checked: false,
  });

  if (current) {
    choices.push({
      kind: 'clear',
      key: 'mark-clear',
      label: 'Clear mark',
      icon: Eraser,
      iconClassName: 'text-muted-foreground',
      mark: null,
      checked: false,
    });
  }

  return choices;
}
